import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";

import { usePostMutation, usePutMutation } from "slices/api";

import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import { filterEmptyStrings } from "utils";
import { locationMasterDataTypes } from "common/constant";

const EditMasterDataModal = ({
  selectedMasterDataType,
  editItem,
  isMasterDataAddable,
  getEndpoint,
  setEditItem,
  t,
}) => {
  const [addMasterData, { isLoading: isAddingData }] = usePostMutation();
  const [updateMasterData, { isLoading: isUpdatingData }] = usePutMutation();

  const {
    dependedFieldsForAddData,
    validation,
    id,
    endpoint: selectedMasterDataEndPoint,
    updateDataEndpoint,
    isQueryParam,
    label,
    tags,
  } = selectedMasterDataType;

  const initialValues = useMemo(
    () =>
      dependedFieldsForAddData.reduce(
        (acc, { fieldKey, fieldName, isCheckBox }) => {
          const fieldValue = fieldKey
            ? get(editItem, fieldKey)
            : editItem[fieldName];
          return {
            ...acc,
            [fieldName]: fieldValue ?? (isCheckBox ? false : ""),
          };
        },
        {}
      ),
    [selectedMasterDataType, editItem]
  );

  const validationSchema = useMemo(
    () =>
      validation ??
      dependedFieldsForAddData.reduce(
        (acc, { fieldName, field, options, type, isCheckBox }) => {
          return {
            ...acc,
            [fieldName]: isCheckBox
              ? Yup.boolean()
              : options || type === "number"
              ? Yup.string().required(t(`${field} is required`))
              : Yup.string()
                  .required(t(`${field} is required`))
                  .matches(
                    /^[\w]+[\w\s.,-]*[\w\s.,-]+/,
                    t(`Please enter valid ${field}`)
                  ),
          };
        },
        {}
      ),
    [selectedMasterDataType, editItem]
  );

  const endpoint = useMemo(
    () =>
      updateDataEndpoint && !locationMasterDataTypes.includes(id)
        ? updateDataEndpoint
        : selectedMasterDataEndPoint,
    [selectedMasterDataType]
  );

  const isEdit = useMemo(() => !isEmpty(editItem), [editItem]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,

    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, { resetForm }) => {
      const key = isQueryParam ? "query" : "payload";
      const payload = filterEmptyStrings(values);
      isEdit
        ? await updateMasterData({
            endpoint: getEndpoint(editItem.id),
            toastMessage: `${label} updated successfully`,
            [key]: payload,
            tags: tags,
          })
        : await addMasterData({
            endpoint,
            toastMessage: `${label} added successfully`,
            [key]: payload,
            tags: tags,
          });
      resetForm();
      setEditItem({});
    },
  });

  const { values, setFieldValue } = formik;

  useEffect(() => {
    formik.setTouched({}, false);
  }, [selectedMasterDataType]);

  return (
    <>
      <Grid container columnSpacing={2} className="mt-2">
        {dependedFieldsForAddData?.map(
          (
            {
              field,
              fieldName,
              options,
              type,
              getOptionLabel,
              getOptionValue,
              isCheckBox,
            },
            index
          ) =>
            isCheckBox ? (
              <Grid item key={index} xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!get(values, fieldName)}
                      onChange={() =>
                        setFieldValue(fieldName, !get(values, fieldName))
                      }
                    />
                  }
                  label={t("Temporary Connection")}
                />
              </Grid>
            ) : options ? (
              <Grid item key={index} xs={12} md={6}>
                <SelectField
                  label={t(`${field}`) + "*"}
                  name={`${fieldName}`}
                  formik={formik}
                  options={options}
                  disabled={isMasterDataAddable}
                  getOptionLabel={getOptionLabel ?? ((option) => option.name)}
                  getOptionValue={getOptionValue ?? ((option) => option.id)}
                />
              </Grid>
            ) : (
              <Grid item key={index} xs={12} md={6}>
                <InputFieldText
                  key={index}
                  label={t(`${field}`) + "*"}
                  name={`${fieldName}`}
                  formik={formik}
                  disabled={isMasterDataAddable}
                  type={type ?? "text"}
                  placeholder={`Enter ${field}`}
                />
              </Grid>
            )
        )}
        <Grid item sm={12} md={6} xs={6}>
          <div className="d-flex">
            <Button
              variant="contained"
              color="primary"
              onClick={formik.handleSubmit}
              disabled={isMasterDataAddable}
              className="mb-3"
            >
              {t(isEdit ? "Edit" : "Add")}{" "}
              {(isAddingData || isUpdatingData) && (
                <CircularProgress size={20} />
              )}
            </Button>
            {isEdit && (
              <Button
                variant="contained"
                color="error"
                onClick={() => setEditItem({})}
                disabled={isMasterDataAddable}
                className="mb-3 ms-3"
              >
                {t("Cancel")}
              </Button>
            )}
          </div>
        </Grid>
      </Grid>
    </>
  );
};

EditMasterDataModal.propTypes = {
  selectedMasterDataType: PropTypes.object,
  editItem: PropTypes.object,
  isMasterDataAddable: PropTypes.bool,
  getEndpoint: PropTypes.func,
  setEditItem: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(EditMasterDataModal);
